/*
  Строка окна «Кому и как предъявляется требование» (26.10.2026, макет Б2):
  отметка, кто это, откуда и как с него взыскивать.

  Способ взыскания показывается и у снятого собственника с долей: его доля
  по-прежнему образует помещение, и человек должен видеть, какая доля не будет
  взыскиваться. У снятого без доли способ не нужен - его в составе нет.
*/

import { useId } from 'react'

import { date, plural } from '../api/format.js'
import { Check } from './Common.jsx'
import Dropdown from './Select.jsx'

export const HOW_HEAD_ID = 'claim-choice-how'

const MODES = {
  solidary: 'солидарно',
  share: 'в доле',
  joint: 'солидарно с собственником',
}

/** «Солидарно с собственником» - только когда есть другой собственник с долей. */
function modesFor(holders, key) {
  const others = Object.keys(holders).filter((holder) => holder !== key)
  if (others.length) return MODES
  return { solidary: MODES.solidary, share: MODES.share }
}

function Badges({ person, claimed }) {
  return (
    <>
      {person.blocked ? <span className="tag tag--muted">банкротство</span> : null}
      {/* Несовершеннолетие определяет сервер - тем же правилом, что обмен с 1С. */}
      {claimed && person.is_minor ? (
        <span className="tag tag--warn">{plural(person.age, 'год', 'года', 'лет')} на дату пакета</span>
      ) : null}
      {claimed && person.age === null ? (
        <span className="tag tag--warn">дата рождения неизвестна</span>
      ) : null}
      {person.left_on ? <span className="tag tag--muted">выбыл {date(person.left_on)}</span> : null}
    </>
  )
}

function How({ person, pick, holders, onChange, labelledBy, disabled }) {
  if (!pick.claimed && pick.mode !== 'share') return <span className="muted">-</span>
  const owners = Object.fromEntries(
    Object.entries(holders).filter(([key]) => key !== person.key),
  )
  return (
    <div className="row">
      <Dropdown
        value={pick.mode}
        onChange={(mode) => onChange({ mode, joint_with: pick.joint_with || Object.keys(owners)[0] })}
        options={modesFor(holders, person.key)}
        labelledBy={labelledBy}
        disabled={disabled}
      />
      {pick.mode === 'share' ? (
        <span className="field field--xs">
          <input
            className="field__input"
            value={pick.share ?? ''}
            placeholder="1/2"
            aria-label={`Доля: ${person.full_name}`}
            onChange={(event) => onChange({ share: event.target.value })}
            disabled={disabled}
          />
        </span>
      ) : null}
      {pick.mode === 'joint' ? (
        <Dropdown
          value={pick.joint_with ?? ''}
          onChange={(joint_with) => onChange({ joint_with })}
          options={owners}
          empty="выберите собственника"
          labelledBy={labelledBy}
          disabled={disabled}
        />
      ) : null}
    </div>
  )
}

export default function ClaimChoiceRow({ person, pick, holders, onChange, locked }) {
  const nameId = useId()
  const disabled = Boolean(locked)
  return (
    <tr>
      <td>
        <Check
          checked={pick.claimed}
          onChange={(claimed) => onChange({ claimed })}
          title={`Предъявлять требование: ${person.full_name}`}
          disabled={disabled || Boolean(person.blocked)}
        />
      </td>
      <td>
        <div id={nameId} className={pick.claimed ? undefined : 'muted'}>
          {person.full_name}
        </div>
        <Badges person={person} claimed={pick.claimed} />
      </td>
      <td data-label="Дата рождения">{date(person.birth_date)}</td>
      <td data-label="Кем приходится">{person.relation || '-'}</td>
      <td data-label="Откуда">{person.origin === 'debtor' ? 'должник счёта' : 'домовая книга'}</td>
      <td data-label="Как взыскивать">
        <How
          person={person}
          pick={pick}
          holders={holders}
          onChange={onChange}
          labelledBy={`${HOW_HEAD_ID} ${nameId}`}
          disabled={disabled}
        />
      </td>
    </tr>
  )
}
